import { useMemo, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import type { TicketDetail } from '@/api/ticketsApi'
import { ApiError } from '@/api/request'
import { Field, NativeSelect } from '@/components/common/Field'
import { errorMessage } from '@/components/common/States'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { useResolveTicket, useTicketLookup } from '@/hooks/useTickets'
import {
  DEVIATION_DECISIONS,
  GXP_IMPACTS,
  RESOLUTION_CATEGORIES,
  initialResolveValues,
  normalizeTicketKey,
  serverProblems,
  summaryTemplate,
  validateResolve,
  type ResolveField,
  type ResolveFormValues,
} from '@/lib/resolveForm'
import { viewerTimeZone } from '@/lib/threadView'

interface ResolveDialogProps {
  ticket: TicketDetail
  routeId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Resolve without e-signature (plan §11): summary (sent to the customer),
 * category (Duplicate asks for the ticket number), root cause (defects), GxP
 * impact, deviation decision and an audited reason. The server re-validates and
 * answers 422 `{ problems }`, shown inline.
 */
export function ResolveDialog({ ticket, routeId, open, onOpenChange }: ResolveDialogProps) {
  const { t } = useTranslation()
  const template = useMemo(
    () => summaryTemplate({ cause: t('resolve.templateCause'), fix: t('resolve.templateFix'), prevention: t('resolve.templatePrevention') }),
    [t],
  )
  const [values, setValues] = useState<ResolveFormValues>(() => initialResolveValues(ticket, template))
  const [errors, setErrors] = useState<Partial<Record<ResolveField, string>>>({})
  const [problems, setProblems] = useState<string[] | null>(null)
  const [lookupError, setLookupError] = useState<string | null>(null)
  const [looking, setLooking] = useState(false)
  const resolve = useResolveTicket(routeId)
  const lookup = useTicketLookup()

  const isDefect = ticket.type.toLowerCase() === 'defect'
  const set = (key: ResolveField, value: string) => setValues((v) => ({ ...v, [key]: value }))
  const err = (key: ResolveField) => (errors[key] ? t(errors[key] as string) : null)

  function reset() {
    setValues(initialResolveValues(ticket, template))
    setErrors({})
    setProblems(null)
    setLookupError(null)
    resolve.reset()
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setProblems(null)
    setLookupError(null)
    const found = validateResolve(values, ticket.type, template)
    setErrors(found)
    if (Object.keys(found).length > 0) return

    let duplicateOfId: string | undefined
    if (values.category === 'duplicate') {
      const key = normalizeTicketKey(values.duplicateOf) as string
      setLooking(true)
      try {
        duplicateOfId = (await lookup(key)).id
      } catch (error) {
        setLookupError(
          error instanceof ApiError && error.status === 404 ? t('resolve.duplicateNotFound', { key }) : errorMessage(error),
        )
        return
      } finally {
        setLooking(false)
      }
    }

    resolve.mutate(
      {
        summary: values.summary.trim(),
        category: values.category,
        rootCause: values.rootCause.trim() || undefined,
        gxpImpact: values.gxpImpact,
        deviationDecision: values.deviationDecision || undefined,
        deviationRef: values.deviationRef.trim() || undefined,
        duplicateOfId,
        reason: values.reason.trim(),
        timeZone: viewerTimeZone(),
      },
      {
        onSuccess: () => {
          onOpenChange(false)
          reset()
        },
        onError: (error) => setProblems(serverProblems(error)),
      },
    )
  }

  const pending = resolve.isPending || looking

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o)
        if (!o) reset()
      }}
    >
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('resolve.title', { key: ticket.key })}</DialogTitle>
          <DialogDescription>{t('resolve.description')}</DialogDescription>
        </DialogHeader>
        <form className="grid grid-cols-2 gap-4" onSubmit={(e) => void onSubmit(e)} noValidate>
          <Field id="rs-summary" label={t('resolve.summary')} help={t('resolve.summaryHelp')} error={err('summary')} className="col-span-2">
            <Textarea id="rs-summary" rows={5} value={values.summary} onChange={(e) => set('summary', e.target.value)} />
          </Field>

          <Field id="rs-category" label={t('resolve.category')} error={err('category')}>
            <NativeSelect id="rs-category" value={values.category} onChange={(e) => set('category', e.target.value)}>
              <option value="">{t('common.select')}</option>
              {RESOLUTION_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {t(`ticket.category.${c}`)}
                </option>
              ))}
            </NativeSelect>
          </Field>
          {values.category === 'duplicate' ? (
            <Field id="rs-duplicate" label={t('resolve.duplicateOf')} error={err('duplicateOf') ?? lookupError}>
              <Input
                id="rs-duplicate"
                className="font-mono"
                placeholder={t('resolve.duplicatePlaceholder')}
                value={values.duplicateOf}
                onChange={(e) => set('duplicateOf', e.target.value)}
              />
            </Field>
          ) : (
            <div />
          )}

          <Field
            id="rs-root"
            label={isDefect ? t('resolve.rootCauseRequired') : `${t('resolve.rootCause')} (${t('common.optional')})`}
            error={err('rootCause')}
            className="col-span-2"
          >
            <Textarea id="rs-root" rows={2} value={values.rootCause} onChange={(e) => set('rootCause', e.target.value)} />
          </Field>

          <Field id="rs-gxp" label={t('resolve.gxpImpact')} error={err('gxpImpact')}>
            <NativeSelect id="rs-gxp" value={values.gxpImpact} onChange={(e) => set('gxpImpact', e.target.value)}>
              <option value="">{t('common.select')}</option>
              {GXP_IMPACTS.filter((g) => g !== 'unassessed').map((g) => (
                <option key={g} value={g}>
                  {t(`ticket.gxp.${g}`)}
                </option>
              ))}
            </NativeSelect>
          </Field>
          <Field id="rs-deviation" label={`${t('resolve.deviationDecision')} (${t('common.optional')})`}>
            <NativeSelect id="rs-deviation" value={values.deviationDecision} onChange={(e) => set('deviationDecision', e.target.value)}>
              <option value="">{t('common.none')}</option>
              {DEVIATION_DECISIONS.map((d) => (
                <option key={d} value={d}>
                  {t(`ticket.deviation.${d}`)}
                </option>
              ))}
            </NativeSelect>
          </Field>
          {values.deviationDecision === 'raised' ? (
            <Field id="rs-devref" label={t('resolve.deviationRef')} error={err('deviationRef')} className="col-span-2">
              <Input
                id="rs-devref"
                className="font-mono"
                placeholder={t('escalate.refPlaceholder')}
                value={values.deviationRef}
                onChange={(e) => set('deviationRef', e.target.value)}
              />
            </Field>
          ) : null}

          <Field id="rs-reason" label={t('resolve.reason')} error={err('reason')} className="col-span-2">
            <Input
              id="rs-reason"
              placeholder={t('resolve.reasonPlaceholder')}
              value={values.reason}
              onChange={(e) => set('reason', e.target.value)}
            />
          </Field>

          {problems ? (
            <div role="alert" className="col-span-2 rounded-md bg-status-escalated-bg px-3 py-2 text-meta text-status-escalated-fg">
              <p className="font-medium">{t('resolve.serverProblems')}</p>
              <ul className="list-disc pl-5">
                {problems.map((p) => (
                  <li key={p}>{p}</li>
                ))}
              </ul>
            </div>
          ) : resolve.error ? (
            <p role="alert" className="col-span-2 rounded-md bg-status-escalated-bg px-3 py-2 text-meta text-status-escalated-fg">
              {t('common.error', { message: errorMessage(resolve.error) })}
            </p>
          ) : null}

          <DialogFooter className="col-span-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={pending}>
              {pending ? t('resolve.submitting') : t('resolve.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
